// Private, account-scoped local selections survive navigation; never store object URLs or tokens.
export function createPhotoDrafts(storage) {
  let owner = '', queue = Promise.resolve()
  const run = action => { const result = queue.then(action); queue = result.catch(() => {}); return result }
  return {
    setOwner(next) {
      owner = next || ''
      const captured = owner
      return run(() => storage.retain(captured))
    },
    read(account, role) {
      return run(async () => {
        if (!account || account !== owner) return []
        const items = await storage.read(account, role)
        return account === owner ? items : []
      })
    },
    save(account, role, angle, photo) {
      return run(async () => {
        if (!account || account !== owner) return
        await storage.save({ key: [account, role, angle].join('|'), account, role, angle,
          name: photo.name, base64: photo.data.base64, contentType: photo.data.contentType, bytes: photo.data.bytes })
      })
    },
    remove(account, role, angle) {
      return run(() => account && account === owner ? storage.remove([account, role, angle].join('|')) : undefined)
    },
  }
}

export function indexedPhotoStorage(indexedDB) {
  let database
  const open = () => {
    if (!database) database = new Promise((resolve, reject) => {
      if (!indexedDB) { reject(new Error('PHOTO_DRAFT_UNAVAILABLE')); return }
      const request = indexedDB.open('detian-private-photo-drafts', 1)
      request.onupgradeneeded = () => request.result.createObjectStore('photos', { keyPath: 'key' })
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => { database = null; reject(request.error) }
    })
    return database
  }
  const transaction = async (mode, action) => {
    const db = await open()
    return new Promise((resolve, reject) => {
      const tx = db.transaction('photos', mode), store = tx.objectStore('photos')
      let result
      tx.oncomplete = () => resolve(result)
      tx.onerror = tx.onabort = () => reject(tx.error || new Error('PHOTO_DRAFT_UNAVAILABLE'))
      action(store, value => { result = value })
    })
  }
  return {
    save: item => transaction('readwrite', store => store.put(item)),
    remove: key => transaction('readwrite', store => store.delete(key)),
    read: (account, role) => transaction('readonly', (store, done) => {
      const request = store.getAll()
      request.onsuccess = () => done(request.result.filter(item => item.account === account && item.role === role))
    }),
    retain: account => transaction('readwrite', store => {
      const request = store.openCursor()
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) return
        if (!account || cursor.value.account !== account) cursor.delete()
        cursor.continue()
      }
    }),
  }
}

export function restorePhotoDraft(item) {
  const binary = atob(item.base64), bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const preview = new Blob([bytes], { type: item.contentType })
  return { name: item.name, data: { base64: item.base64, contentType: item.contentType, bytes: bytes.length, preview }, url: URL.createObjectURL(preview) }
}
